import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import SignInButton from './sign-in';
import './index.css';
import LoyaltyCardPreview from './loyalty-card-preview';
import { constructEarningRule, isLightColor } from './utils';
import ProgressComponent from './progress-component';
import getImage from 'helpers/getImage';
import { LOYALTY_DEFAULTS } from './constants';
import {
  BREAKPOINT_VALUES,
  LOGIN_FROM_TYPE,
  LOYALTY_VIEW_INDEX_TYPE,
  ORDER_STAGE_TYPES,
  VISIBILITY_INDEX_TYPE,
} from 'helpers/enums';
import { setState, toggleMobileCart } from 'containers/Home/actions';
import { useAuth } from 'contexts/AuthProvider';
import {
  assignPointsToUserScan,
  createLoyaltyUserSignup,
  showEnrollmentForm,
} from './actions';
import starsBackground from '../../assets/images/loyalty-presignup-slide-stars.svg';
import discountIcon from '../../assets/images/loyalty-presignup-discount.svg';
import {
  CloseButtonCWUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import { useLoyaltyRoute } from 'helpers/hooks/useLoyaltyRoute';
import getPriceForDisplay from 'helpers/getPriceForDisplay';

export default function GuestUser({
  breakPoint,
  storeConfig,
  preSignupData,
  currency,
  storeSettings,
  themeColors,
  pointsTerminology,
  setHomeState,
  userLoyaltyDetails,
  setIsEnrollmentFormOpen,
  storeLoyaltySettings,
}) {
  const dispatch = useDispatch();
  const { storeId, restaurantId } = storeConfig;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentRewardCard, setCurrentRewardCard] = useState(0);
  const [isCardAnimating, setIsCardAnimating] = useState(false);
  const [autoSlideEnabled, setAutoSlideEnabled] = useState(true);
  const carouselRef = useRef(null);
  const rewardCardsRef = useRef(null);
  const { setState: setAuthState, setAuthOrderStage, setLoginFrom } = useAuth();
  const { isLoyalty, orderId, referralId } = useLoyaltyRoute();
  const { menuItems, populars, userDetails, currentUser, currentOrder } = useSelector(
    (state) => state.homeReducer
  );
  const { halfHalfSetup } = useSelector((state) => state.layoutReducer);

  // Function to calculate points for an item based on earning rules
  const calculatePointsForItem = (item) => {
    const earningRules = preSignupData?.earnings || [];
    const itemPrice = getPriceForDisplay(item, false, [], [], { orderType: currentOrder?.orderType });
    let totalPoints = 0;

    earningRules.forEach((rule) => {
      switch (rule.type?.toString()) {
        case '1': // AMOUNT - Points per amount spent
          if (rule.amount && rule.amount > 0) {
            totalPoints += Math.floor(itemPrice / rule.amount) * rule.points;
          }
          break;
        case '4': // ITEM_PURCHASED - Points per item purchased
          totalPoints += rule.points;
          break;
        case '0': // ORDER - Points per order
          if (rule.minOrderAmount && rule.minOrderAmount > 0) {
            // Only award points if order meets minimum amount
            if (itemPrice >= rule.minOrderAmount) {
              totalPoints += rule.points;
            }
            // If doesn't meet minimum, no points (implicit)
          } else {
            // No minimum order amount, always award points
            totalPoints += rule.points;
          }
          break;
        default:
          break;
      }
    });

    return totalPoints;
  };

  const containerStyle = {
    backgroundColor: themeColors.backgroundColor,
  };

  const textStyle = {
    color: themeColors.mainTextColor,
  };

  const headingColorStyle = {
    color: themeColors.labelTextColor,
  };
  const isLightBackground = isLightColor(themeColors.backgroundColor);
  const rewardHeaderTextColor = isLightBackground
    ? themeColors.mainTextColor
    : '#fff';
  const rewardButtonBackground = isLightBackground
    ? themeColors.mainTextColor
    : themeColors.backgroundColor;

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
    setDragOffset(0);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 2 : prev - 1));
    setDragOffset(0);
  };

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === 2 ? 0 : prev + 1));
    setDragOffset(0);
  };

  const goToNextRewardCard = () => {
    const redemptions = preSignupData?.redemptions || [];
    if (redemptions.length > 1 && !isCardAnimating) {
      setIsCardAnimating(true);
      setTimeout(() => {
        setCurrentRewardCard((prev) =>
          prev === redemptions.length - 1 ? 0 : prev + 1
        );
        setIsCardAnimating(false);
      }, 500);
    }
  };

  useEffect(() => {
    const redemptions = preSignupData?.redemptions || [];
    if (redemptions.length > 1 && currentSlide === 1) {
      const interval = setInterval(goToNextRewardCard, 2000);
      return () => clearInterval(interval);
    }
  }, [currentSlide, preSignupData?.redemptions, isCardAnimating]);

  // Auto-slide functionality
  useEffect(() => {
    let intervalId;

    if (autoSlideEnabled && !isSwiping) {
      intervalId = setInterval(() => {
        goToNextSlide();
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentSlide, autoSlideEnabled, isSwiping]);

  // Pause auto-sliding when user interacts with carousel
  const pauseAutoSlide = () => {
    setAutoSlideEnabled(false);

    // Resume auto-sliding after 5 seconds of inactivity
    const timeoutId = setTimeout(() => {
      setAutoSlideEnabled(true);
    }, 5000);

    return timeoutId;
  };

  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      setDragOffset(eventData.deltaX);
      setIsSwiping(true);
      pauseAutoSlide();
    },
    onSwipedLeft: () => {
      goToNextSlide();
      setIsSwiping(false);
    },
    onSwipedRight: () => {
      goToPrevSlide();
      setIsSwiping(false);
    },
    onTouchEndOrOnMouseUp: () => {
      setIsSwiping(false);
      setDragOffset(0);
    },
    trackMouse: true,
    preventDefaultTouchmoveEvent: true,
  });

  const handleMouseDown = (e) => {
    setTouchStartX(e.clientX);
    setIsSwiping(true);
    pauseAutoSlide();
  };

  const handleMouseMove = (e) => {
    if (isSwiping) {
      const currentX = e.clientX;
      const diff = currentX - touchStartX;
      setDragOffset(diff);
    }
  };

  const handleMouseUp = () => {
    if (isSwiping) {
      if (dragOffset > 100) {
        goToPrevSlide();
      } else if (dragOffset < -100) {
        goToNextSlide();
      }
      setIsSwiping(false);
      setDragOffset(0);
    }
  };

  const handleMouseLeave = () => {
    if (isSwiping) {
      setIsSwiping(false);
      setDragOffset(0);
    }
  };

  const signInHandler = () => {
    dispatch(toggleMobileCart(true));
    dispatch(setState({ loyaltyOpen: false }));
    setAuthState({ loginFrom: LOGIN_FROM_TYPE.LOYALTY });
    if (!userDetails) {
      setAuthState({ authOrderStage: ORDER_STAGE_TYPES.OAUTH_FORM });
    }
  };

  const handleClose = () => {
    dispatch(setState({ loyaltyOpen: false }));
    dispatch(toggleMobileCart(false));
    setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
  };

  const loyaltySignupHandler = async () => {
    if (userDetails && userLoyaltyDetails === null && storeLoyaltySettings) {
      const enrollmentFields = storeLoyaltySettings.enrollmentFields;
      if (enrollmentFields && enrollmentFields.length > 0) {
        dispatch(showEnrollmentForm(true));
      } else {
        const { intlPhoneNumber, firstName, lastName, email, _id } =
          userDetails;
        const userEmail = email ? email : currentUser.email;
        // do loyalty signup
        const loyaltySignupPayload = {
          mobileNumber: intlPhoneNumber,
          firstName,
          lastName,
          email: userEmail,
          userId: _id,
          storeId,
          restaurantId,
          settingId: storeLoyaltySettings.id,
          level: storeLoyaltySettings?.level || LOYALTY_DEFAULTS.LEVEL,
        };

        // Only include referralId if it exists and is not empty
        if (referralId && referralId.trim() !== '') {
          loyaltySignupPayload.referralId = referralId;
        }

        const loyaltySignupResponse = await dispatch(
          createLoyaltyUserSignup(loyaltySignupPayload)
        );
        if (isLoyalty && orderId) {
          dispatch(
            assignPointsToUserScan({
              orderId,
              mobileNumber: intlPhoneNumber,
              restaurantId,
              userId: loyaltySignupResponse.userId,
              storeId,
            })
          )
            .then(() => {
              // Mark this orderId as processed so we don't call again on re-render or remount.
              sessionStorage.setItem(`loyaltyScan_${orderId}`, '1');
            })
            .catch((err) => {
              console.error('Failed to assign points for loyalty scan:', err);
            });
        }
      }
    }
  };

  // Reset dragOffset when slide changes or component unmounts
  useEffect(() => {
    return () => {
      setDragOffset(0);
      setIsSwiping(false);
    };
  }, [currentSlide]);

  const getRandomItems = (items, count) => {
    if (!items || items.length === 0) return [];

    const shuffled = [...items].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const topItems = useMemo(() => {
    let items = [];
    const randomPopulars = getRandomItems(populars || [], 3);

    if (randomPopulars.length < 3 && menuItems && menuItems.length > 0) {
      const neededCount = 3 - randomPopulars.length;
      const randomMenuItems = getRandomItems(menuItems || [], neededCount);
      items = [...randomPopulars, ...randomMenuItems];
    } else {
      items = randomPopulars;
    }

    return items.slice(0, 3);
  }, [populars, menuItems]);

  const ruleStrings = useMemo(() => {
    const earningRules = preSignupData?.earnings?.slice(0, 3) || [];

    return earningRules.map((rule) => constructEarningRule(rule, currency));
  }, [preSignupData?.earnings, currency]);

  const firstSlideContent = useMemo(() => {
    return (
      <>
        <div className='slide-heading slide-1-heading'>
          <h2 style={headingColorStyle}>
            Earn {pointsTerminology} with every order
          </h2>
          <p style={textStyle}>
            {ruleStrings.length > 0 ? (
              ruleStrings.map((ruleText, index) => (
                <span key={index}>
                  {ruleText}.<br />
                </span>
              ))
            ) : (
              <span style={textStyle}>
                Earn {pointsTerminology} with every order.
              </span>
            )}
          </p>
        </div>

        <div className='loyalty-slide1-inner'>
          <div className='menu-container'>
            <div className='menu-container-heading'>Order</div>
            {topItems.length > 0 &&
              topItems.map((item, index) => {
                const itemPrice = (
                  getPriceForDisplay(item, false, [], [], { orderType: currentOrder?.orderType }));
                const image = getImage(item.mediaPath || item.urlS3);
                return (
                  <React.Fragment key={item._id || index}>
                    <div className='menu-item'>
                      <img
                        className='menu-item-image'
                        src={image}
                        alt={item.name}
                      />
                      <div className='menu-item-text'>
                        <div className='menu-item-info'>
                          <div className='menu-item-name'>
                            {item.productName || item.name}
                          </div>
                          <div className='menu-item-price'>
                            {currency} {itemPrice}
                          </div>
                        </div>
                        <div className='menu-item-points'>
                          <div className='menu-item-points-value'>
                            {calculatePointsForItem(item)} {pointsTerminology} ↑
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < topItems.length - 1 && (
                      <hr
                        style={{
                          margin: '2px 0',
                          border: 'none',
                          height: '1px',
                          backgroundColor: '#eee',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </>
    );
  }, [
    pointsTerminology,
    headingColorStyle,
    textStyle,
    currency,
    storeSettings,
    halfHalfSetup,
    topItems,
    ruleStrings,
    preSignupData,
  ]);

  const secondSlideContent = useMemo(() => {
    const redemptions = preSignupData?.redemptions || [];

    const nextCardIndex =
      redemptions.length > 1
        ? currentRewardCard === redemptions.length - 1
          ? 0
          : currentRewardCard + 1
        : null;

    return (
      <>
        <div className='slide-heading slide-2-heading'>
          <h2 style={headingColorStyle}>
            Redeem {pointsTerminology} for free items or discounts
          </h2>
          <p style={textStyle} className='w-full'>
            {/* Lorem ipsum dolor sit amet, ligula suspendisse nulla pretium */}
          </p>
        </div>
        <div className='loyalty-slide2-inner' ref={rewardCardsRef}>
          {redemptions.length > 0 ? (
            <div className='reward-cards-carousel'>
              <div
                className={`reward-card-stack current-card ${isCardAnimating ? 'cycling-out' : ''
                  }`}
              >
                <div className='rewardCard'>
                  <div
                    className='rewardCardHeader'
                    style={{
                      backgroundColor: themeColors.backgroundColor,
                      color: rewardHeaderTextColor,
                      borderBottom: isLightBackground
                        ? '1px solid rgba(0, 0, 0, 0.08)'
                        : 'none',
                    }}
                  >
                    <div
                      className='rewardCardTitle'
                      style={{ color: rewardHeaderTextColor }}
                    >
                      Exclusive offer
                    </div>
                    <div className='rewardCardIcon'>
                      <img src={discountIcon} alt='Discount' />
                    </div>
                  </div>
                  <div className='rewardCardBody'>
                    <div className='rewardCardDescription'>
                      <div className='rewardCardDescriptionTitle'>
                        {redemptions[currentRewardCard]?.name}
                      </div>
                      <div className='rewardCardDescriptionAction'>
                        <div className='rewardCardDescriptionActionButtonWrapper'>
                          <div
                            className='rewardCardDescriptionActionButton'
                            style={{
                              backgroundColor: rewardButtonBackground,
                              color: '#fff',
                            }}
                          >
                            Redeem
                          </div>
                        </div>
                        <div className='rewardCardPointsValue'>
                          {redemptions[currentRewardCard]?.points}{' '}
                          {pointsTerminology}
                        </div>
                      </div>
                    </div>
                    <div className='rewardCardImage'>
                      <img
                        src={
                          redemptions[currentRewardCard]?.imageUrl ||
                          storeSettings?.themeSettings?.imageThemeUrl
                        }
                        alt={redemptions[currentRewardCard]?.name || 'Reward'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {redemptions.length > 1 && nextCardIndex !== null && (
                <div
                  className={`reward-card-stack next-card ${isCardAnimating ? 'cycling-in' : ''
                    }`}
                >
                  <div className='rewardCard'>
                    <div
                      className='rewardCardHeader'
                      style={{
                        backgroundColor: themeColors.backgroundColor,
                        color: rewardHeaderTextColor,
                        borderBottom: isLightBackground
                          ? '1px solid rgba(0, 0, 0, 0.08)'
                          : 'none',
                      }}
                    >
                      <div
                        className='rewardCardTitle'
                        style={{ color: rewardHeaderTextColor }}
                      >
                        Exclusive offer
                      </div>
                      <div className='rewardCardIcon'>
                        <img
                          src={discountIcon}
                          alt='Discount'
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    </div>
                    <div className='rewardCardBody'>
                      <div className='rewardCardDescription'>
                        <div className='rewardCardDescriptionTitle'>
                          {redemptions[nextCardIndex]?.name}
                        </div>
                        <div className='rewardCardDescriptionAction'>
                          <div className='rewardCardDescriptionActionButtonWrapper'>
                            <div
                              className='rewardCardDescriptionActionButton'
                              style={{
                                backgroundColor: rewardButtonBackground,
                                color: '#fff',
                              }}
                            >
                              Redeem
                            </div>
                          </div>
                          <div className='rewardCardPointsValue'>
                            {redemptions[nextCardIndex]?.points}{' '}
                            {pointsTerminology}
                          </div>
                        </div>
                      </div>
                      <div className='rewardCardImage'>
                        <img
                          src={
                            redemptions[nextCardIndex]?.imageUrl ||
                            storeSettings?.themeSettings?.imageThemeUrl
                          }
                          alt={redemptions[nextCardIndex]?.name || 'Reward'}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className='reward-card-fallback' style={textStyle}>
              No special rewards available right now.
            </div>
          )}
        </div>
      </>
    );
  }, [
    pointsTerminology,
    headingColorStyle,
    textStyle,
    isLightBackground,
    rewardHeaderTextColor,
    rewardButtonBackground,
    storeSettings,
    preSignupData,
    currentRewardCard,
    isCardAnimating,
  ]);

  const thirdSlideContent = useMemo(
    () => (
      <>
        <div className='slide-heading slide-3-heading'>
          <h2 style={headingColorStyle}>
            Save your loyalty card to Apple Wallet or Google Wallet
          </h2>
          <p style={textStyle} className='w-full'>
            {/* Lorem ipsum dolor sit amet, ligula suspendisse nulla pretium. */}
          </p>
        </div>

        <div className='loyalty-benefits'>
          <div className='loyaltyPreviewCardContainer'>
            <div className='loyaltyCardPreviewBackground'>
              <div className='loyaltyCardBackgroundContent'>
                <div className='loyaltyCardName'>Loyalty</div>
                <hr className='loyaltyCardBalanceSeparator' />
                <div className='loyaltyCardBalanceBody'>
                  <div className='loyaltyCardBalance'>
                    {pointsTerminology} balance
                  </div>
                  <div className='loyaltyCardPoints'>7,600</div>
                  <ProgressComponent
                    currentPoints={7600}
                    maxPoints={10000}
                    fillColor={themeColors.backgroundColor}
                    labelTextColor={themeColors.mainTextColor}
                  />
                </div>
              </div>
            </div>
            <LoyaltyCardPreview
              mainTextColor={themeColors.mainTextColor}
              labelColor={themeColors.labelTextColor}
              bgColor={themeColors.backgroundColor}
              selectedImageTheme={storeSettings?.themeSettings?.imageThemeUrl}
            />
          </div>
        </div>
      </>
    ),
    [
      pointsTerminology,
      headingColorStyle,
      textStyle,
      themeColors,
      storeSettings,
    ]
  );

  const getTransformValue = () => {
    const baseTransform = -(currentSlide * 100);
    const dragPercentage = carouselRef.current
      ? (dragOffset / carouselRef.current.offsetWidth) * 100
      : 0;
    return `translateX(${baseTransform + dragPercentage}%)`;
  };

  // Pause auto-sliding when clicking on dots
  const handleDotClick = (slideIndex) => {
    goToSlide(slideIndex);
    const timeoutId = pauseAutoSlide();
    return () => clearTimeout(timeoutId);
  };
  const isLoyaltyScan = isLoyalty && orderId;
  return (
    <div className='loyalty-content-wrapper'>
      <CloseButtonCWUI
        extendedClassName={`ml-5 ${breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
          }`}
        onClick={handleClose}
      />
      <div className='loyalty-guest-container'>
        <CustomerCategoryTitleUI extendedClassName='main-heading'>
          {isLoyaltyScan ? 'Sign up to earn reward points' : 'Get Rewarded.'}
        </CustomerCategoryTitleUI>
        <div
          className='loyalty-guest-container-inner'
          style={{
            ...containerStyle,
            '--stars-background-image': `url(${starsBackground})`,
          }}
        >
          <div
            className='loyalty-carousel'
            ref={carouselRef}
            {...swipeHandlers}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className='loyalty-carousel-inner'
              style={{
                transform: getTransformValue(),
                transition: isSwiping ? 'none' : 'transform 0.5s ease',
              }}
            >
              <div className='loyalty-carousel-slide slide-1'>
                {firstSlideContent}
              </div>

              <div className='loyalty-carousel-slide slide-2'>
                {secondSlideContent}
              </div>

              <div className='loyalty-carousel-slide slide-3'>
                {thirdSlideContent}
              </div>
            </div>
          </div>
        </div>
        <ul className='loyalty-carousel-dots'>
          {[0, 1, 2].map((slideIndex) => (
            <li
              key={slideIndex}
              className={`loyalty-carousel-dot ${currentSlide === slideIndex ? 'active' : ''
                }`}
              style={{
                backgroundColor:
                  currentSlide === slideIndex
                    ? themeColors.backgroundColor
                    : '#ccc',
              }}
              onClick={() => handleDotClick(slideIndex)}
            />
          ))}
        </ul>
        <SignInButton
          userLoyaltyDetails={userLoyaltyDetails}
          userDetails={userDetails}
          onClick={signInHandler}
          onLoyaltySignUp={loyaltySignupHandler}
          themeColors={themeColors}
        />
      </div>
    </div>
  );
}
